import type { Languages } from '@s4l/i18n/types/Languages'
import type { Config } from '../../_config'
import { FactoryCookie } from '../../_cookie/FactoryCookie'
import type { Countries } from '../../_kernel/Country'
import { DomainError } from '../../_kernel/DomainError'
import { ErrorsCode } from '../../_kernel/ErrorsCode'
import { PriceType } from '../../_kernel/PriceType'
import type { AffiliateType } from '../domain/Affiliate'
import { Affiliate } from '../domain/Affiliate'
import { Language } from '../domain/Language'
import { Market } from '../domain/Market'
import type { ALLOWED_MARKETS, Markets } from '../domain/Market'
import type { PartialSearch } from '../domain/PartialSearch'
import type { SharedRepository } from '../domain/SharedRepository'
import type { Source } from '../domain/Source'
import type { FeatureFlag } from '../domain/FeatureFlag'

type COOKIE_TRACK = {
  adults: string
  aid: AffiliateType
  ci: string
  co: string
  children: string
  lang: Languages
  market: Markets
  country: Countries
  slug: string
  rm?: 'all_in' | 'all_in_daily' | 'net' | 'gross' | 'trivago_net' | undefined
}
export class CookieSharedRepository implements SharedRepository {
  static MARKET_COOKIE_NAME = 'sfl_mkt'
  static COOKIE_LANGUAGE = 'lang'
  static COOKIE_TRACK = 'track'

  async source(): Promise<Source> {
    throw DomainError.withCodes(ErrorsCode.FORBIDDEN_REPOSITORY_METHOD)
  }

  static create({ config }: { config: Config }) {
    return new CookieSharedRepository(config)
  }

  constructor(private readonly config: Config) {}

  async market() {
    const cookie = await FactoryCookie.create({ config: this.config }).cookie()

    const [error, market] = cookie.get<(typeof ALLOWED_MARKETS)[number]>({
      key: CookieSharedRepository.MARKET_COOKIE_NAME,
    })

    if (error) return Market.empty()
    if (!market) return Market.empty()

    return Market.create({ value: market })
  }

  async language() {
    const cookie = await FactoryCookie.create({ config: this.config }).cookie()

    const [error, language] = cookie.get<Languages>({ key: CookieSharedRepository.COOKIE_LANGUAGE })

    if (error) return Language.empty()
    if (!language) return Language.empty()

    return Language.create({ value: language })
  }

  async partialSearch(): Promise<PartialSearch> {
    throw DomainError.withCodes(ErrorsCode.FORBIDDEN_REPOSITORY_METHOD)
  }

  async affiliate(): Promise<Affiliate> {
    const cookie = await FactoryCookie.create({ config: this.config }).cookie()

    const [error, trackJson] = cookie.get<string>({
      key: CookieSharedRepository.COOKIE_TRACK,
      encoding: 'base64',
    })

    if (!trackJson) return Affiliate.empty()
    if (error) return Affiliate.empty()

    try {
      const track = JSON.parse(trackJson) as COOKIE_TRACK
      if (!track.aid) return Affiliate.empty()

      return Affiliate.create({
        value: track.aid,
        priceType: PriceType.createOrEmptyFromCookie({ value: track.rm }),
      })
    } catch (e) {
      return Affiliate.empty()
    }
  }

  async featureFlagByName(): Promise<FeatureFlag> {
    throw DomainError.withCodes(ErrorsCode.FORBIDDEN_REPOSITORY_METHOD)
  }
}
